<script setup lang="ts">
import type { PropType } from 'vue';
import { SwiperSlide } from 'swiper/vue';
import { Navigation, Grid } from 'swiper/modules';
import type { EventInfo } from '@/types/event';
import type { TranslatableEvent } from '@/types/translation';
import type { NavigationOptions, SwiperOptions } from 'swiper/types';
import type { EventSearchQuery, FeaturedEventQuery } from '@/types/search';
import type { PaginationInfo } from '@/types/common';
import dotsLeft from '@/assets/images/dots-left.jpg';
import dotsRight from '@/assets/images/dots-right.jpg';

import { transformEventSearchResponse } from '../../transforms/search';
import { eventTranslationConfig } from '../../translation/configs/eventConfig';
import { TranslatableWrapper, createTranslatableWrappers } from '../../translation/core/TranslatableWrapper';

const swiperModules = [Navigation, Grid];

const ITEMS_PER_PAGE = {
	MOBILE: 4,
	DESKTOP: 6,
} as const;

const router = useRouter();
const profileStore = useProfileStore();
const searchStore = useSearchStore();
const { isMobile } = useWindowResize();
const { trackEvent } = useGtm();

const props = defineProps({
	logoSrc: {
		type: String,
		default: '',
	},
	logoAlt: {
		type: String,
		default: '',
	},
	trackingSection: {
		type: String,
		default: '',
	},
	showDotsDecoration: {
		type: Boolean,
		default: false,
	},
	title: {
		type: String,
		default: "What's happening in Japan right now",
	},
	subtitle: {
		type: String,
		default: 'Explore the Japan that most people never see.',
	},
	fetchMode: {
		type: String as PropType<'featured' | 'search'>,
		default: 'featured',
	},
	featuredQuery: {
		type: Object as PropType<Partial<FeaturedEventQuery>>,
		default: () => ({}),
	},
	searchQuery: {
		type: Object as PropType<Partial<EventSearchQuery>>,
		default: () => ({}),
	},
});

// Translation service singleton
const translationService = useTranslationService();

// Unique translation scope per component instance
const instanceId = `je-${Math.random().toString(36).slice(2)}`;
const isTranslatedView = ref(false);
const isTranslating = ref(false);

// Store wrappers for translation
const eventWrappers = shallowRef<TranslatableWrapper<EventInfo>[]>([]);

// Convert wrapper to TranslatableEvent for EventCardItem compatibility
function wrapperToTranslatableEvent(wrapper: TranslatableWrapper<EventInfo>): TranslatableEvent {
	const source = wrapper.source;
	return {
		id: source.id,
		name: {
			original: wrapper.getOriginal('name'),
			translated: wrapper.getTranslated('name'),
		},
		groupName: source.groupName ? {
			original: wrapper.getOriginal('groupName'),
			translated: wrapper.getTranslated('groupName'),
		} : null,
		address: {
			original: wrapper.getOriginal('address'),
			translated: wrapper.getTranslated('address'),
		},
		city: source.city ? {
			original: wrapper.getOriginal('city'),
			translated: wrapper.getTranslated('city'),
		} : undefined,
		state: source.state ? {
			original: wrapper.getOriginal('state'),
			translated: wrapper.getTranslated('state'),
		} : undefined,
		locationType: source.locationType,
		status: source.status,
		coverImageUrl: source.coverImageUrl,
		displayDate: source.displayDate,
		displayTime: source.displayTime,
		displayTimezone: source.displayTimezone,
		isHappeningNow: source.isHappeningNow,
		avatarUrl: source.avatarUrl,
		organizer: source.organizer,
		organizerId: source.organizerId,
		isEventSeries: source.isEventSeries,
		isEventFree: source.isEventFree,
		isSoldout: source.isSoldout,
		groupId: source.groupId,
	};
}

// Get translatable events for template
const translatedEvents = computed(() => eventWrappers.value.map(wrapperToTranslatableEvent));

// Convert a TranslatableEvent back to EventInfo for components that expect it
function toEventInfo(event: TranslatableEvent): EventInfo {
	return {
		id: event.id,
		name: event.name.original,
		groupName: event.groupName?.original,
		address: event.address?.original || '',
		city: event.city?.original,
		state: event.state?.original,
		locationType: event.locationType,
		status: event.status,
		coverImageUrl: event.coverImageUrl,
		displayDate: event.displayDate,
		displayTime: event.displayTime,
		displayTimezone: event.displayTimezone,
		isHappeningNow: event.isHappeningNow,
		avatarUrl: event.avatarUrl,
		organizer: event.organizer,
		organizerId: event.organizerId,
		isEventSeries: event.isEventSeries,
		isEventFree: event.isEventFree,
		isSoldout: event.isSoldout,
		groupId: event.groupId,
	};
}

const paginationRef = ref<{ handleClickNext: () => void }>();
const carouselRef = ref<{ handleSlideNext: () => void; handleSlidePrev: () => void }>();
const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);
const previousCurrentPage = ref<number | null>(null);
const currentPage = ref(1);
const isDisabledPrev = ref(false);
const isDisabledNext = ref(false);

const localPagination = ref<PaginationInfo | null>(null);
const isLoading = ref(false);
const isHomePage = computed(() => router.currentRoute.value.name === ROUTE.INDEX.NAME);
const itemsPerPage = computed(() => (isMobile.value ? ITEMS_PER_PAGE.MOBILE : ITEMS_PER_PAGE.DESKTOP));

// Use events from wrappers
const events = computed(() => translatedEvents.value.map(toEventInfo));

async function translateEvents() {
	const context = `japan-events-${instanceId}__${currentPage.value}`;
	const allRequests: { key: string; text: string }[] = [];

	eventWrappers.value.forEach((wrapper) => {
		const requests = wrapper.collectForTranslation(context);
		requests.forEach((req) => allRequests.push(req));
	});

	if (allRequests.length > 0) {
		const results = await translationService.translate(allRequests);

		// Apply translations back to wrappers - trigger reactivity
		const updatedWrappers = [...eventWrappers.value];
		updatedWrappers.forEach((wrapper) => {
			wrapper.applyTranslations(results, context);
		});
		eventWrappers.value = updatedWrappers;
	}

	// Fire GTM translation success event
	trackEvent({
		event: GTM_EVENT_NAME.TRANSLATION_SUCCESS,
		user_id: profileStore.profileId,
		content_type: TRANSLATION_CONTENT_TYPE.HOME,
	});
}

async function fetchEvents() {
	// if previous current page is equal to new current page, stop fetching
	if (currentPage.value === previousCurrentPage.value) {
		isDisabledNext.value = true;
		return;
	}

	try {
		isLoading.value = true;
		let list: EventInfo[] = [];
		if (props.fetchMode === 'featured') {
			const { data, errorMessage } = await searchStore.fetchFeaturedEventsOnce({
				currentPage: currentPage.value,
				itemPerPage: itemsPerPage.value,
				...(props.featuredQuery || {}),
			} as FeaturedEventQuery);
			if (errorMessage.value) {
				console.error('Failed to fetch featured events:', errorMessage.value);
			}
			if (data.value) {
				localPagination.value = data.value.paginationInfo;
				list = transformEventSearchResponse(data.value.data);
			}
		} else {
			const { data, errorMessage } = await searchStore.fetchEventsBySearchOnce({
				currentPage: currentPage.value,
				itemPerPage: itemsPerPage.value,
				...(props.searchQuery || {}),
			} as EventSearchQuery);
			if (errorMessage.value) {
				console.error('Failed to search events:', errorMessage.value);
			}
			if (data.value) {
				localPagination.value = data.value.paginationInfo;
				list = transformEventSearchResponse(data.value.data);
			}
		}

		// Create wrappers for new events
		const newWrappers = createTranslatableWrappers(list, eventTranslationConfig);

		// Append or replace wrappers based on page
		if (currentPage.value === 1) {
			eventWrappers.value = newWrappers;
		} else {
			eventWrappers.value = [...eventWrappers.value, ...newWrappers];
		}

		previousCurrentPage.value = currentPage.value;
	} catch (error) {
		console.error('Failed to fetch events:', error);
	} finally {
		isLoading.value = false;
	}

	// If translation mode is active, translate new events for this page
	if (isTranslatedView.value) {
		await translateEvents();
	}
}

async function handleTranslateToggle() {
	if (isTranslatedView.value) {
		isTranslatedView.value = false;
		return;
	}

	isTranslating.value = true;

	try {
		await translateEvents();
		isTranslatedView.value = true;
	} catch (error) {
		console.error('Translation failed:', error);
	} finally {
		isTranslating.value = false;
	}
}

function handlePageChange(page: number) {
	currentPage.value = Number(page);
	fetchEvents();
}

function handleOnReachEnd() {
	if (isDisabledNext.value) {
		return;
	}
	paginationRef.value?.handleClickNext();
}

function handleClickNext() {
	if (isDisabledNext.value) {
		return;
	}
	carouselRef.value?.handleSlideNext();
}

function handleClickPrev() {
	if (isDisabledPrev.value) {
		return;
	}
	carouselRef.value?.handleSlidePrev();
}

function handleOnReachBeginning() {
	isDisabledPrev.value = true;
}

function handleOnSlideNext() {
	isDisabledPrev.value = false;
}

function handleOnSlidePrev() {
	isDisabledNext.value = false;
}

const swiperOptions: NavigationOptions & SwiperOptions = {
	slidesPerGroup: 2,
	freeMode: {
		enabled: true,
		sticky: false,
	},
	roundLengths: true,
	slidesPerView: 'auto',
	grid: {
		rows: 2,
		fill: 'row',
	},
	spaceBetween: 16,
	navigation: {
		prevEl: prevEl.value,
		nextEl: nextEl.value,
	},
	breakpoints: {
		768: {
			slidesPerGroup: 3,
			slidesPerView: 3,
			freeMode: false,
		},
	},
	grabCursor: true,
	a11y: {
		prevSlideMessage: 'Previous page',
		nextSlideMessage: 'Next page',
	},
};

function updatePaginationButtons() {
	const pagination = localPagination.value;
	if (!pagination || pagination.totalPages === 0) {
		// Don't update disabled states until we have valid pagination info
		return;
	}

	const newPage = currentPage.value;

	// Disable previous button only on first or second page
	isDisabledPrev.value = newPage <= 2;
	// Disable next button only on last page
	isDisabledNext.value = newPage >= pagination.totalPages + 1;
}

watch(
	currentPage,
	() => {
		updatePaginationButtons();
	},
	{ immediate: true },
);

watch(
	localPagination,
	() => {
		updatePaginationButtons();
	},
	{ immediate: true },
);

watch(isMobile, () => {
	// Reset pagination state when viewport changes to prevent grid misalignment
	currentPage.value = 1;
	previousCurrentPage.value = null;
	eventWrappers.value = [];
	localPagination.value = null;
	isDisabledPrev.value = false;
	isDisabledNext.value = false;
	fetchEvents();
});

onBeforeUnmount(() => {
	// nothing to reset as we use local state
});

onMounted(() => {
	fetchEvents();
});
</script>

<template>
	<section class="japan-events-section">
		<template v-if="props.showDotsDecoration">
			<img class="dots-decoration dots-decoration--left" :src="dotsLeft" alt="" />
			<img class="dots-decoration dots-decoration--right" :src="dotsRight" alt="" />
		</template>
		<div class="inner-section">
			<div class="section-header">
				<div class="header-left">
					<div
						v-if="props.logoSrc"
						:class="[
							'section-logo',
							{ '--first': !!props.subtitle, '--peachan': props.logoAlt === 'Peachan' },
						]"
					>
						<img :src="props.logoSrc" :alt="props.logoAlt" />
					</div>
					<div class="header-text">
						<h2 class="title text-semibold">{{ props.title }}</h2>
						<p v-if="props.subtitle" class="sub-title text-regular">{{ props.subtitle }}</p>
					</div>
				</div>
				<div class="header-right">
					<template v-if="isLoading">
						<ButtonSkeletonLoader width="180" height="36" />
					</template>
					<template v-else>
						<BaseButton
							v-if="translatedEvents?.length"
							class="translation-button"
							variant="outlined"
							color="gray"
							size="sm"
							type="button"
							:disabled="isTranslating"
							@click="handleTranslateToggle"
						>
							<TranslateIcon :color="isTranslating ? ICON_COLOR.DISABLED : ICON_COLOR.DEFAULT" />
							{{ isTranslatedView ? 'Undo translation' : 'Translate this section' }}
						</BaseButton>
					</template>
				</div>
			</div>

			<template v-if="translatedEvents?.length">
				<div class="event-carousel-container">
					<BaseCarousel
						:key="`carousel-${isMobile}`"
						ref="carouselRef"
						class="event-carousel"
						:swiper-options="swiperOptions"
						:modules="swiperModules"
						@on-reach-end="handleOnReachEnd"
						@on-reach-beginning="handleOnReachBeginning"
						@on-slide-next="handleOnSlideNext"
						@on-slide-prev="handleOnSlidePrev"
					>
						<SwiperSlide
							v-for="event in translatedEvents"
							:key="event.id"
							class="event-carousel-slide"
						>
							<EventCardItem
								:translatable-data="event"
								:is-translated="isTranslatedView"
								:tracking-section="props.trackingSection"
							/>
						</SwiperSlide>
					</BaseCarousel>
					<div
						v-show="!isDisabledPrev"
						ref="prevEl"
						class="no-select pagination-left-icon"
						@click="handleClickPrev"
					>
						<PaginationLeftIcon :color="ICON_COLOR.DEFAULT" />
					</div>
					<div
						v-show="!isDisabledNext"
						ref="nextEl"
						class="no-select pagination-right-icon"
						@click="handleClickNext"
					>
						<PaginationRightIcon :color="ICON_COLOR.DEFAULT" />
					</div>
				</div>
			</template>
			<template v-else>
				<MultipleSkeletonLoader
					v-if="isLoading"
					class="event-carousel-skeleton-loader"
					:rows="isMobile ? 2 : 6"
				>
					<ImageSkeletonLoader height="280" />
				</MultipleSkeletonLoader>
				<NoDataFound v-else class="no-data-container">
					<template #content>
						<h1 class="text-md text-semibold">No events found</h1>
						<p class="text-sm text-regular">Please check back soon to find events in Japan.</p>
					</template>
				</NoDataFound>
			</template>
			<div class="paginate">
				<BasePagination
					v-if="events?.length"
					ref="paginationRef"
					:page="currentPage"
					:total-pages="localPagination?.totalPages"
					:is-loading="isLoading"
					@on-page-change="handlePageChange"
				/>
			</div>
			<div v-if="isHomePage" class="footer-container">
				<RouterLink :to="{ name: ROUTE.VISIT_JAPAN.NAME }">
					<BaseButton> Find Japanese events </BaseButton>
				</RouterLink>
			</div>
		</div>
	</section>
</template>

<style lang="scss" scoped>
.japan-events-section {
	position: relative;
	padding-top: spacings-get(12);
	background-color: colors-get(base, white);
	overflow: hidden;

	.dots-decoration {
		position: absolute;
		top: 0;
		width: min(rem(320), 26vw);
		object-fit: cover;
		opacity: 0.5;
		pointer-events: none;
		user-select: none;
		z-index: 0;

		@include media-query-max(mobile) {
			top: 0;
			width: rem(180);
			height: rem(140);
			opacity: 0.35;
		}
	}

	.dots-decoration--left {
		left: 0;
		object-position: top left;
	}

	.dots-decoration--right {
		right: 0;
		object-position: top right;
	}

	.inner-section {
		position: relative;
		z-index: 1;
		max-width: rem(1128);
		padding: 0 spacings-get(4);
		margin: 0 auto;
	}

	.section-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: spacings-get(6);
		padding: 0 rem(60);

		@include media-query-max(mobile) {
			flex-direction: column;
			gap: spacings-get(4);
			padding: 0;
		}

		.header-left {
			display: flex;
			align-items: center;
			gap: spacings-get(3);
		}

		.section-logo {
			width: rem(30);
			height: rem(30);
			display: flex;
			align-items: center;
			justify-content: center;
			overflow: hidden;
			flex-shrink: 0;

			&.--peachan {
				width: rem(60);
				height: rem(60);
				padding: rem(4);

				img {
					object-fit: contain;
				}
			}

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}

		.header-text {
			text-align: left;

			.title {
				color: #4b4b4b;
				margin: 0 0 spacings-get(1);
				@include fonts-get(semibold, display-sm);
				text-wrap: wrap;
				word-break: normal;

				@include media-query-max(mobile) {
					@include fonts-size(display-xs);
				}
			}

			.sub-title {
				color: colors-get(gray, 800);
				margin: 0;
				@include fonts-get(regular, text-md);

				@include media-query-max(mobile) {
					@include fonts-size(text-sm);
				}
			}
		}

		.header-right {
			flex-shrink: 0;
		}
	}

	.event-carousel-container {
		position: relative;
		margin: 0 rem(60);

		@include media-query-max(mobile) {
			margin: 0;
		}

		.event-carousel {
			height: rem(576);
			padding: 0 0 spacings-get(2);

			@include media-query-max(mobile) {
				:deep(.swiper-slide) {
					width: rem(320);
				}
			}
		}

		.pagination-left-icon,
		.pagination-right-icon {
			position: absolute;
			display: block;
			top: 50%;
			transform: translateY(-50%);
			cursor: pointer;

			@include media-query-max(mobile) {
				display: none;
			}
		}

		.pagination-left-icon {
			left: rem(-60);
		}

		.pagination-right-icon {
			right: rem(-60);
		}

		:deep(.swiper-button-prev),
		:deep(.swiper-button-next) {
			display: none;
		}
	}
}

.event-carousel-skeleton-loader {
	margin: 0 rem(60);
	display: grid !important;
	grid-template-columns: repeat(3, 1fr);
	grid-template-rows: repeat(2, 1fr);
	padding: 0 0 spacings-get(2);

	@include media-query-max(mobile) {
		margin: 0;
		grid-template-columns: repeat(1, 1fr);
		grid-template-rows: repeat(2, 1fr);
	}
}

.no-data-container {
	svg {
		margin: 0;
	}

	h1 {
		@include fonts-get(bold, 'text-xl');
	}

	p {
		@include fonts-get(regular, 'text-md');
	}
}

.paginate {
	display: none;
}

.footer-container {
	padding: spacings-get(12) 0;
}
</style>
